import { useCallback, useContext, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Link,
  Redirect,
  Route,
  Switch,
  matchPath,
  useHistory,
  useLocation,
} from 'react-router-dom'
import { useShallow } from 'zustand/react/shallow'

import Button from '../../Button'
import StepsBar, { type TStepsBarProps } from '../../StepsBar'
import Title from '../../Title'
import BrandingContext from '../../../context/BrandingContext'
import Analyse from './Analyse'
import Build from './Build'
import Checkout from './Checkout'
import useNewUpload from '../../../hooks/useNewUpload'
import { PrivateAPI } from '../../../utils/api'

const ANALYSE_FORM_ID = 'new-upload-analyse-form'
const BUILD_AUTO_FORM_ID = 'new-upload-build-auto-form'
const BUILD_MANUAL_FORM_ID = 'new-upload-build-manual-form'
const CHECKOUT_FORM_ID = 'new-upload-checkout-form'

const getAnalyseLocation = (pathname: string) => ({
  pathname,
  state: {
    shouldResumeAnalysis: false,
  },
})

type TNexusAvailabilityResponse = {
  isNexusAvailable: boolean
}

const Upload = () => {
  const history = useHistory()
  const location = useLocation()
  const [branding] = useContext(BrandingContext)
  const { primaryColorPalette } = branding
  const {
    analysedFile,
    createUploadDisabled,
    isAnalyseSubmitting,
    isBuildNextDisabled,
    isCreatingCheckout,
    isNexusAvailable,
    isSavingBuild,
    manualRequested,
    resetNewUpload,
    setNewUploadState,
  } = useNewUpload(
    useShallow((state) => ({
      analysedFile: state.analysedFile,
      createUploadDisabled: state.checkout.createUploadDisabled,
      isAnalyseSubmitting: state.analyse.isSubmitting,
      isBuildNextDisabled: state.build.nextDisabled,
      isCreatingCheckout: state.checkout.isCreating,
      isNexusAvailable: state.isNexusAvailable,
      isSavingBuild: state.build.isSaving,
      manualRequested: state.manualRequested,
      resetNewUpload: state.reset,
      setNewUploadState: state.setState,
    })),
  )
  const analyseEditMatch = matchPath<{ fileId: string }>(location.pathname, {
    path: '/uploads/new/:fileId',
    exact: true,
  })

  const buildMatch = matchPath<{ fileId: string }>(location.pathname, {
    path: '/uploads/new/:fileId/build',
    exact: true,
  })

  const checkoutMatch = matchPath<{ fileId: string }>(location.pathname, {
    path: '/uploads/new/:fileId/checkout',
    exact: true,
  })

  const fileId =
    analyseEditMatch?.params.fileId ||
    buildMatch?.params.fileId ||
    checkoutMatch?.params.fileId ||
    ''

  const isAnalyse =
    location.pathname === '/uploads/new' || Boolean(analyseEditMatch)

  const isBuild = Boolean(buildMatch)
  const isCheckout = Boolean(checkoutMatch)
  const analysePath = fileId ? `/uploads/new/${fileId}` : '/uploads/new'

  const buildPath = fileId ? `/uploads/new/${fileId}/build` : '/uploads/new'

  const checkoutPath = fileId
    ? `/uploads/new/${fileId}/checkout`
    : '/uploads/new'
  const analyseLocation = getAnalyseLocation(analysePath)

  const isManualBuild = Boolean(
    isBuild &&
    analysedFile &&
    (manualRequested ||
      ((analysedFile.auto?.availableDTCs.length || 0) === 0 &&
        (analysedFile.auto?.availableMods.length || 0) === 0)),
  )

  const steps: TStepsBarProps['steps'] = [
    {
      id: 'prepare',
      name: 'Prepare upload',
      desc: 'For detailed analysis',
      to: analyseLocation,
      current: isAnalyse,
      complete: isBuild || isCheckout,
    },
    {
      id: 'build',
      name: 'Build your file',
      desc: 'By selecting tune options',
      to: buildPath,
      current: isBuild,
      complete: isCheckout,
    },
    {
      id: 'checkout',
      name: 'Checkout',
      desc: 'And submit for processing',
      to: checkoutPath,
      current: isCheckout,
      complete: false,
    },
  ]

  const nexusAvailabilityQuery = useQuery({
    queryKey: ['nexus-availability'],
    queryFn: async () => {
      const response = await PrivateAPI<TNexusAvailabilityResponse>({
        url: '/uploads/nexus-availability',
        method: 'get',
      })

      return response.data
    },
  })

  useEffect(() => {
    if (typeof nexusAvailabilityQuery.data?.isNexusAvailable !== 'boolean') {
      return
    }

    setNewUploadState({
      isNexusAvailable: nexusAvailabilityQuery.data.isNexusAvailable,
    })
  }, [nexusAvailabilityQuery.data?.isNexusAvailable, setNewUploadState])

  const isLoadingNexusAvailability =
    isAnalyse &&
    nexusAvailabilityQuery.isLoading &&
    !nexusAvailabilityQuery.data

  const renderButtons = () => {
    if (isAnalyse) {
      return (
        <div className="flex items-center justify-between">
          <Button color="white" as={Link} to="/uploads/list/all">
            Cancel
          </Button>
          <Button
            color="primary"
            type="submit"
            form={ANALYSE_FORM_ID}
            disabled={isAnalyseSubmitting}
          >
            {isNexusAvailable ? 'Analyse' : 'Next'}
          </Button>
        </div>
      )
    }

    if (isManualBuild) {
      return (
        <div className="flex items-center justify-between">
          <Button
            color="white"
            disabled={isSavingBuild || isBuildNextDisabled}
            onClick={() => history.push(analyseLocation)}
          >
            Back
          </Button>
          <Button
            color="primary"
            type="submit"
            form={BUILD_MANUAL_FORM_ID}
            disabled={isSavingBuild || isBuildNextDisabled}
          >
            {isSavingBuild ? 'Saving...' : 'Next'}
          </Button>
        </div>
      )
    }

    if (isBuild) {
      return (
        <div className="flex items-center justify-between">
          <Button
            color="white"
            disabled={isSavingBuild || isBuildNextDisabled}
            onClick={() => history.push(analyseLocation)}
          >
            Back
          </Button>
          <Button
            color="primary"
            type="submit"
            form={BUILD_AUTO_FORM_ID}
            disabled={isSavingBuild || isBuildNextDisabled}
          >
            {isSavingBuild ? 'Saving...' : 'Next'}
          </Button>
        </div>
      )
    }

    if (isCheckout) {
      return (
        <div className="flex items-center justify-between">
          <Button
            color="white"
            disabled={isCreatingCheckout}
            onClick={() => history.push(buildPath)}
          >
            Back
          </Button>
          <Button
            color="primary"
            type="submit"
            form={CHECKOUT_FORM_ID}
            className={createUploadDisabled ? 'opacity-50' : undefined}
            disabled={isCreatingCheckout || createUploadDisabled}
          >
            {isCreatingCheckout ? (
              <>
                <span
                  className="spinner mr-2"
                  style={{
                    width: 14,
                    height: 14,
                    borderWidth: 2,
                    borderColor: `var(--color-${primaryColorPalette}-200)`,
                    borderLeftColor: '#ffffff',
                  }}
                />
                Creating...
              </>
            ) : (
              'Create upload'
            )}
          </Button>
        </div>
      )
    }

    return null
  }

  const handleAnalyseSuccess = useCallback(
    (fileId: string) => {
      // To ensure the browser back button restores the inputs state
      history.replace(getAnalyseLocation(`/uploads/new/${fileId}`))
      history.push(`/uploads/new/${fileId}/build`)
    },
    [history],
  )

  const handleBuildManualSubmitSuccess = useCallback(() => {
    history.push(`/uploads/new/${fileId}/checkout`)
  }, [fileId, history])

  const handleBuildAutoSubmitSuccess = useCallback(() => {
    history.push(`/uploads/new/${fileId}/checkout`)
  }, [fileId, history])

  useEffect(() => {
    return () => {
      resetNewUpload()
    }
  }, [resetNewUpload])

  return (
    <>
      <div className="bg-white">
        <Title title="New upload" />
      </div>
      <div className="max-w-5xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-xs">
          <StepsBar steps={steps} />
          <div className="border-x border-gray-200">
            {isLoadingNexusAvailability ? (
              <div className="flex flex-1 items-center justify-center p-12">
                <span className="spinner" />
              </div>
            ) : (
              <Switch>
                <Route
                  exact
                  path="/uploads/new"
                  render={() => (
                    <Analyse
                      formId={ANALYSE_FORM_ID}
                      onSubmitSuccess={handleAnalyseSuccess}
                    />
                  )}
                />
                <Route
                  exact
                  path="/uploads/new/:fileId"
                  render={() => (
                    <Analyse
                      formId={ANALYSE_FORM_ID}
                      onSubmitSuccess={handleAnalyseSuccess}
                    />
                  )}
                />
                <Route
                  exact
                  path="/uploads/new/:fileId/build"
                  render={() => (
                    <Build
                      buildAutoFormId={BUILD_AUTO_FORM_ID}
                      buildManualFormId={BUILD_MANUAL_FORM_ID}
                      onBuildAutoSubmitSuccess={handleBuildAutoSubmitSuccess}
                      onBuildManualSubmitSuccess={handleBuildManualSubmitSuccess}
                    />
                  )}
                />
                <Route
                  exact
                  path="/uploads/new/:fileId/checkout"
                  render={() => <Checkout formId={CHECKOUT_FORM_ID} />}
                />
                <Redirect to="/uploads/new" />
              </Switch>
            )}
          </div>
          <div className="border border-gray-200 rounded-b-lg px-4 py-4">
            {isLoadingNexusAvailability ? null : renderButtons()}
          </div>
        </div>
      </div>
    </>
  )
}

export default Upload
